import { lazy, type ReactElement, Suspense, useState } from 'react'

import { AccountOverview } from './accounts/account-overview.js'
import { clearBounceCount } from './auth/bounce-guard.js'
import { APP_HANDOFF_ENABLED, redirectToApp } from './auth/handoff.js'
import { useAppView } from './auth/use-app-view.js'
import { InstallNudge } from './install/install-nudge.js'
import { resolveFallbackScreenCopy } from './install/install-shell-copy.js'
import { InstallShell } from './install/install-shell.js'
import { I18nProvider } from './providers/i18n/index.js'
import { ChunkLoadBoundary, ChunkLoadRecovered } from './recovery/chunk-load-boundary.js'

/**
 * Last-resort surface when the auth chunk cannot be loaded even after an auto-reload (a persistent
 * network failure, or a genuinely missing asset). Plain, dependency-free copy — the lazy chunk that
 * carries i18n is exactly what failed — with a manual retry so the user is never stuck on a blank
 * screen.
 */
const AuthLoadError = (): ReactElement => {
  // Same featherweight en/vi copy as the install shell (PT-5679): the lingui runtime is exactly what
  // failed to load here, so it cannot be used. Resolved once via a lazy initializer.
  const [copy] = useState(resolveFallbackScreenCopy)
  return (
    <main className="shell">
      <h1 className="shell__title">TauroMarkets</h1>
      <p className="shell__lead">{copy.authLoadErrorLead}</p>
      <button className="shell__cta" type="button" onClick={() => window.location.reload()}>
        {copy.reload}
      </button>
    </main>
  )
}

/**
 * The auth surface (login / sign-up / password recovery) is loaded on demand: its shared layout
 * pulls in react-aria, lingui, Panda, and react-query, none of which belong on the
 * install-critical path. The chunk is fetched only once a live API domain is adopted and the
 * `login` view is reached.
 */
const AuthScreens = lazy(() => import('./auth/auth-screens.js'))

/**
 * Shown when the handoff has bounced between `/` and `/app` too many times to be converging (a
 * permanently blocked storage or a permanently broken API host — PT-5648). Plain, dependency-free
 * copy, like {@link AuthLoadError}, so it renders even when nothing but the shell has loaded. Retry
 * clears the shared bounce counter first so the reload starts from a clean window instead of
 * re-tripping on its first hop.
 */
const HandoffUnavailable = (): ReactElement => {
  // Dependency-free copy like {@link AuthLoadError} — only the precached shell is guaranteed loaded
  // here — resolved once via a lazy initializer (PT-5679).
  const [copy] = useState(resolveFallbackScreenCopy)
  return (
    <main className="shell">
      <h1 className="shell__title">TauroMarkets</h1>
      <p className="shell__lead">{copy.handoffUnavailableLead}</p>
      <button
        className="shell__cta"
        type="button"
        onClick={() => {
          clearBounceCount()
          window.location.reload()
        }}
      >
        {copy.tryAgain}
      </button>
    </main>
  )
}

/**
 * Renders nothing on the happy path; the authenticated session is handed off to the dashboard at
 * `/app` (a same-origin navigation) exactly once, via a lazy `useState` initializer so it fires on
 * the first render of the `authenticated` view and never again on re-render. If the handoff loop has
 * exhausted its bounce budget it navigates nowhere and shows {@link HandoffUnavailable} instead, so a
 * stuck session lands on a real surface rather than a flickering redirect loop.
 */
const AppHandoff = (): null | ReactElement => {
  const [result] = useState(redirectToApp)
  return result === 'exhausted' ? <HandoffUnavailable /> : null
}

/**
 * The app's single top-level surface, chosen by {@link useAppView} from the auth facts:
 *  - `discovering` — boot-time domain discovery is still running; show the install shell, which is
 *    also the right thing to see when the origin is blocked and nothing has resolved yet;
 *  - `unavailable` — discovery exhausted its window with no trusted domain; same shell (its copy
 *    already guides the user to install / retry);
 *  - `login` — a live domain is adopted; render the shared Tauro auth surface (login / sign-up /
 *    recovery, identical to dashboard) behind an i18n provider so it is localized exactly the same way;
 *  - `authenticated` — a bearer session is held; show the live account overview (and, once `/app`
 *    is deployed, hand off to the full dashboard instead).
 */
const App = (): ReactElement => {
  const { onAuthenticated, view } = useAppView()

  switch (view) {
    case 'authenticated':
      // The session is held. Now that /app serves the dashboard (9b-4), leave the PWA and boot it
      // there — it reads the shared bearer (IndexedDB) and API domain (localStorage) this same origin
      // holds. The native AccountOverview stays as the fallback surface should the handoff ever be
      // taken dark again (it self-loops only while /app is undeployed — PT-5598).
      return APP_HANDOFF_ENABLED ? <AppHandoff /> : <AccountOverview />
    case 'login':
      return (
        <I18nProvider>
          {/* The install shell is behind us once discovery succeeds, so the nudge keeps the
              install affordance alive on the login screen — installing is what makes the app
              survive a later block of the delivery origin. */}
          <InstallNudge />
          {/* A redeploy rotates the hashed chunk names, so an old open shell can throw a
              dynamic-import error the moment it lazy-loads AuthScreens — a blank white screen
              without this boundary. It reloads once (picking up the fresh, network-first HTML),
              then shows a retry surface rather than looping. ChunkLoadRecovered sits inside the
              Suspense so its mount — which happens only once the chunk resolves — disarms the guard.
              DO NOT nest another lazy()/import() that can throw into render under this boundary:
              the guard would be disarmed by AuthScreens mounting, then re-armed by the nested
              failure, reloading on a loop. Keep additional lazy boundaries self-contained. */}
          <ChunkLoadBoundary fallback={<AuthLoadError />}>
            <Suspense fallback={<main className="shell" />}>
              <AuthScreens onAuthenticated={onAuthenticated} />
              <ChunkLoadRecovered />
            </Suspense>
          </ChunkLoadBoundary>
        </I18nProvider>
      )
    case 'discovering':
    case 'unavailable':
    default:
      return <InstallShell />
  }
}

export default App
